import * as React from "react";

import type { MediaAssetV1 } from "@/contracts/media/MediaAssetV1";
import { resolveAssetToUrl } from "@/lib/media/resolveMediaAssetToUrl";

const resolvedAssetUrlCache = new Map<string, string | null>();

type MediaPanelPreviewMode = "closed" | "ingest" | "focus" | "overlay";
type MediaPreviewStatus = "idle" | "loading" | "ready" | "error";

type UseMediaPreviewSyncArgs = {
  panelMode: MediaPanelPreviewMode;
  selectedAsset: MediaAssetV1 | null;
  resolvedFocusPreviewUrl: string | null;
};

type UseMediaPreviewSyncResult = {
  previewUrl: string | null;
  previewStatus: MediaPreviewStatus;
  err: string;
};

function readImmediateAssetUrl(asset: MediaAssetV1 | null): string | null {
  if (!asset) return null;

  if (asset.ref.provider === "static") {
    return String(asset.ref.path);
  }

  if (asset.ref.provider === "url") {
    return String(asset.ref.url);
  }

  return null;
}

function getAssetPreviewCacheKey(asset: MediaAssetV1 | null): string | null {
  if (!asset) return null;

  switch (asset.ref.provider) {
    case "static":
      return `static:${asset.ref.path}`;
    case "url":
      return `url:${asset.ref.url}`;
    case "firebase":
      return `firebase:${asset.ref.storagePath}`;
    case "registry":
      return `registry:${asset.ref.key}`;
    default:
      return asset.id;
  }
}

export default function useMediaPreviewSync({
  panelMode,
  selectedAsset,
  resolvedFocusPreviewUrl,
}: UseMediaPreviewSyncArgs): UseMediaPreviewSyncResult {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewStatus, setPreviewStatus] = React.useState<MediaPreviewStatus>("idle");
  const [err, setErr] = React.useState("");
  const requestSeqRef = React.useRef(0);

  const commitPreviewState = React.useCallback(
    (nextUrl: string | null, nextStatus: MediaPreviewStatus, nextErr: string) => {
      setPreviewUrl((prev) => (prev === nextUrl ? prev : nextUrl));
      setPreviewStatus((prev) => (prev === nextStatus ? prev : nextStatus));
      setErr((prev) => (prev === nextErr ? prev : nextErr));
    },
    [],
  );

  React.useEffect(() => {
    const requestId = requestSeqRef.current + 1;
    requestSeqRef.current = requestId;
    let alive = true;

    async function run() {
      if (panelMode === "closed") {
        commitPreviewState(null, "idle", "");
        return;
      }

      const shouldUseFocusPreview =
        (panelMode === "focus" || panelMode === "overlay") && !!resolvedFocusPreviewUrl;

      if (shouldUseFocusPreview) {
        commitPreviewState(resolvedFocusPreviewUrl, "ready", "");
        return;
      }

      if (!selectedAsset) {
        commitPreviewState(null, "idle", "");
        return;
      }

      const immediateUrl = readImmediateAssetUrl(selectedAsset);
      if (immediateUrl) {
        const cacheKey = getAssetPreviewCacheKey(selectedAsset);
        if (cacheKey) {
          resolvedAssetUrlCache.set(cacheKey, immediateUrl);
        }
        commitPreviewState(immediateUrl, "ready", "");
        return;
      }

      const cacheKey = getAssetPreviewCacheKey(selectedAsset);
      if (cacheKey && resolvedAssetUrlCache.has(cacheKey)) {
        const cachedUrl = resolvedAssetUrlCache.get(cacheKey) ?? null;
        commitPreviewState(
          cachedUrl,
          cachedUrl ? "ready" : "error",
          cachedUrl ? "" : "Failed to resolve URL (registry/storage not seeded?).",
        );
        return;
      }

      commitPreviewState(null, "loading", "");

      try {
        const url = await resolveAssetToUrl(selectedAsset);
        if (!alive || requestSeqRef.current !== requestId) return;

        if (!url) {
          if (cacheKey) {
            resolvedAssetUrlCache.set(cacheKey, null);
          }
          commitPreviewState(null, "error", "Failed to resolve URL (registry/storage not seeded?).");
          return;
        }

        if (cacheKey) {
          resolvedAssetUrlCache.set(cacheKey, url);
        }
        commitPreviewState(url, "ready", "");
      } catch (error) {
        if (!alive || requestSeqRef.current !== requestId) return;
        commitPreviewState(null, "error", String(error));
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [panelMode, resolvedFocusPreviewUrl, selectedAsset]);

  return {
    previewUrl,
    previewStatus,
    err,
  };
}